import axios from 'axios';

// In production, use relative URLs (will use same domain as frontend)
// In development, use explicit localhost URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

const api = axios.create({
    baseURL: `${API_BASE_URL}/api`,
    timeout: 3000000, // 50 minutes for large file uploads
});

// Request interceptor - Add authentication token
api.interceptors.request.use(
    (config) => {
        // console.log(`Making ${config.method.toUpperCase()} request to ${config.url}`);

        // Add authentication token if available
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor - Handle authentication errors
api.interceptors.response.use(
    (response) => {
        return response.data;
    },
    (error) => {
        console.error('API Error:', error.response?.data || error.message);

        // Handle authentication errors.
        //
        // Only a request that ACTUALLY CARRIED a token can prove the session is
        // dead. A 401 on a request sent without one (a race on first load
        // before the token is read, a call from a logged-out tab) says nothing
        // about the stored session — and clearing it there is what turns a
        // harmless race into a surprise logout.
        if (error.response?.status === 401) {
            const sentToken = error.config?.headers?.Authorization;
            if (!sentToken) {
                throw new Error('Not authenticated');
            }

            localStorage.removeItem('token');
            localStorage.removeItem('user');

            // Redirect to login — but not if we are already there, which would
            // reload the login page out from under a sign-in attempt.
            if (window.location.pathname !== '/') {
                window.location.href = '/';
            }

            throw new Error('Session expired. Please log in again.');
        }

        // Payment required — not enough balance. The backend's detail string
        // already names the exact amounts in dollars (see the 402s raised by
        // the workflow-start gates), so it is preferred over this fallback.
        if (error.response?.status === 402) {
            const detail = error.response?.data?.detail;
            if (typeof detail === 'object') {
                throw new Error(detail.message || 'Not enough balance');
            }
            throw new Error(detail || 'Not enough balance');
        }

        if (error.response?.data?.error) {
            throw new Error(error.response.data.error);
        }

        if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            throw new Error(detail.message || detail);
        }

        throw new Error(error.message || 'Network error occurred');
    }
);

// ===== USED FUNCTIONS ONLY =====

// Authentication API functions
export const getCurrentUser = async () => {
    const response = await api.get('/auth/me');
    return response.user;
};

export const getUserCredits = async () => {
    const response = await api.get('/auth/credits');
    return response.stats;
};

export const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
};

// ---------------------------------------------------------------------------
// Silent session refresh
//
// The backend issues a 48h token and exposes POST /api/auth/refresh-token,
// which mints a fresh one from a still-valid token — but nothing ever called
// it, so a session simply ran out and dumped the user at the login screen
// mid-task. Refreshing on load and periodically keeps an active user signed in
// indefinitely, while someone who stops using the app still ages out.
//
// Deliberately fails SILENTLY. A refresh is opportunistic: if it fails because
// the network blipped, the existing token is still good and the next attempt
// will do the job. Only a real 401 ends the session, and the interceptor above
// already handles that.
// ---------------------------------------------------------------------------

// Comfortably inside the 48h token life, and frequent enough that a tab left
// open overnight refreshes several times before its token could expire.
const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours
const REFRESH_STORAGE_KEY = 'token-refreshed-at';

export const refreshSession = async () => {
    if (!localStorage.getItem('token')) return false;
    try {
        const response = await api.post('/auth/refresh-token');
        if (response?.token) {
            localStorage.setItem('token', response.token);
            if (response.user) {
                localStorage.setItem('user', JSON.stringify(response.user));
            }
            localStorage.setItem(REFRESH_STORAGE_KEY, String(Date.now()));
            return true;
        }
    } catch (err) {
        // Silent by design — see above.
        console.debug('Token refresh skipped:', err.message);
    }
    return false;
};

/**
 * Refresh now if it has been long enough, then keep refreshing on an interval.
 * Returns a cleanup function.
 *
 * The elapsed-time check matters for the common case of a user who opens
 * several tabs or reloads often: without it every reload would fire a refresh,
 * and with it the token is renewed at most once per interval across all of
 * them (the timestamp is shared through localStorage).
 */
export const startSessionRefresh = () => {
    const last = Number(localStorage.getItem(REFRESH_STORAGE_KEY) || 0);
    if (Date.now() - last > REFRESH_INTERVAL_MS) {
        refreshSession();
    }

    const id = setInterval(refreshSession, REFRESH_INTERVAL_MS);

    // A laptop that sleeps stops the interval; refreshing when the tab becomes
    // visible again covers the gap that would otherwise let a token lapse.
    const onVisible = () => {
        if (document.visibilityState !== 'visible') return;
        const at = Number(localStorage.getItem(REFRESH_STORAGE_KEY) || 0);
        if (Date.now() - at > REFRESH_INTERVAL_MS) refreshSession();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
        clearInterval(id);
        document.removeEventListener('visibilitychange', onVisible);
    };
};

// Credit API functions
export const getCreditStats = async () => {
    const response = await api.get('/auth/credits');
    return response;
};

// Quote a workflow before running it, in cents.
//
// Two modes matching the two entry points: pass `durationSeconds` (measured
// client-side from an uploaded audio file) for an EXACT quote, or `targetWords`
// + `short` for a topic estimate carrying a +/-10% band.
//
// The response also carries `cents_per_minute` / `cents_per_cloud_export` and
// the caller's `balance`/`sufficient`/`shortfall`, so the UI can price, gate and
// explain in one round-trip — and so no rate is ever hardcoded in the frontend.
export const estimateWorkflowCost = async ({ targetWords, short = false, durationSeconds, scriptWords, aiAvatar = false, webCrawl = false } = {}) => {
    const params = {};
    if (aiAvatar) params.ai_avatar = true;
    if (webCrawl) params.web_crawl = true;
    if (durationSeconds != null) params.duration_seconds = durationSeconds;
    else if (scriptWords != null) params.script_words = scriptWords;
    else {
        if (targetWords != null) params.target_words = targetWords;
        params.short = short;
    }
    const response = await api.get('/stock-video/estimate', { params });
    return response;
};

// Payment API functions
export const getCreditPackages = async () => {
    const response = await api.get('/payment/packages');
    return response;
};

export const getSubscriptionPlans = async () => {
    const response = await api.get('/payment/subscription-plans');
    return response;
};

export const getMySubscription = async () => {
    const response = await api.get('/subscriptions/my-subscription');
    return response;
};

export const cancelSubscription = async () => {
    const response = await api.post('/subscriptions/cancel');
    return response;
};

// Undo a pending cancellation. Only valid before the period ends — after that
// Stripe has deleted the subscription and the user must subscribe again.
export const resumeSubscription = async () => {
    const response = await api.post('/subscriptions/resume');
    return response;
};

// Switch an active subscription to another plan. The new plan's full price is
// charged now and its credits follow via the webhook.
export const changeSubscriptionPlan = async (plan) => {
    const response = await api.post('/subscriptions/change-plan', { plan });
    return response;
};

// Stripe's hosted billing portal: card updates, invoices, plan changes. Returns
// a one-time URL to redirect to; it is not a durable link, so it must be
// fetched fresh each time rather than cached.
export const createBillingPortalSession = async () => {
    const response = await api.post('/subscriptions/billing-portal', {
        return_url: `${window.location.origin}/credits`,
    });
    return response;
};

// ===== STOCK WORKFLOWS API FUNCTIONS =====
// Admin-only tool (ported from speedink): topic/audio -> stock footage video,
// with a browser timeline editor for per-scene review before export.

// ===== IMAGE GENERATOR API FUNCTIONS =====
// Admin-only tool: generate a single image from a text prompt (gpt-image-2 via
// fal.ai, falling back to grok-imagine-image on Replicate). The response's
// image_url is a long-lived provider-hosted URL, usable directly as an <img>
// src — there is no backend download endpoint to route through.
export const generateImage = async (prompt) => {
    const response = await api.post('/image-generator/generate', null, {
        params: { prompt }
    });
    return response;
};

// ===== BACKGROUND REMOVAL API FUNCTIONS =====
// Admin-only tool: cut the subject out of an uploaded image (rembg on CPU).
//
// Deliberately NOT routed through the shared `api` instance. That instance's
// response interceptor returns `response.data` and drops everything else, and
// this endpoint's answer is split between the body (the PNG) and two headers
// (X-Processing-Ms, X-Model) — the timing is the whole point of a test page.
// So this uses axios directly and re-implements the two things the interceptor
// would have done: attach the bearer token, and turn a failure into an Error
// carrying the backend's own `detail` string.
export const listBackgroundRemovalModels = async () => {
    const response = await api.get('/background-removal/models');
    return response;
};

export const removeBackground = async (file, { model, alphaMatting = false } = {}) => {
    const formData = new FormData();
    formData.append('image', file);

    const params = new URLSearchParams();
    if (model) params.append('model', model);
    if (alphaMatting) params.append('alpha_matting', 'true');
    const query = params.toString();

    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/background-removal/remove${query ? `?${query}` : ''}`,
            formData,
            {
                responseType: 'blob',
                timeout: 300000,
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            }
        );
        return {
            blob: response.data,
            processingMs: Number(response.headers['x-processing-ms']) || null,
            model: response.headers['x-model'] || model || null,
        };
    } catch (error) {
        // With responseType 'blob' an error body arrives as a Blob too, so the
        // JSON `detail` has to be read back out of it rather than off
        // error.response.data directly.
        const data = error.response?.data;
        if (data instanceof Blob) {
            try {
                const parsed = JSON.parse(await data.text());
                throw new Error(parsed.detail || parsed.error || 'Background removal failed');
            } catch (parseError) {
                if (parseError instanceof Error && parseError.message !== 'Unexpected end of JSON input') {
                    throw parseError;
                }
            }
        }
        throw new Error(error.message || 'Background removal failed');
    }
};

// ===== SRT GENERATOR API FUNCTIONS =====
// Admin-only tool: transcribe an uploaded audio file with the pipeline's own
// WhisperService and get the SRT back.
//
// Same reasoning as removeBackground above for not using the shared `api`
// instance: the answer is split between the body (the SRT text) and a set of
// X- headers (timing, provider, detected language, cue count), and the shared
// response interceptor returns `response.data` alone and drops the headers.
export const generateSrt = async (file, {
    provider,
    language,
    sceneDuration,
    minSceneSeconds,
    maxSceneSeconds,
    unbounded = false,
} = {}) => {
    const formData = new FormData();
    formData.append('audio_file', file);

    const params = new URLSearchParams();
    if (provider) params.append('provider', provider);
    if (language) params.append('language', language);
    if (sceneDuration) params.append('scene_duration', String(sceneDuration));
    if (unbounded) params.append('unbounded', 'true');
    // The bounds are meaningless once unbounded is set, and sending both would
    // read as a contradiction in the request log.
    if (!unbounded && minSceneSeconds) params.append('min_scene_seconds', String(minSceneSeconds));
    if (!unbounded && maxSceneSeconds) params.append('max_scene_seconds', String(maxSceneSeconds));
    const query = params.toString();

    try {
        const response = await axios.post(
            `${API_BASE_URL}/api/srt-generator/generate${query ? `?${query}` : ''}`,
            formData,
            {
                responseType: 'blob',
                // Local whisper on CPU is minutes on a long narration, not
                // seconds — the default timeout would abort a working render.
                timeout: 1800000,
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            }
        );
        return {
            srt: await response.data.text(),
            processingMs: Number(response.headers['x-processing-ms']) || null,
            provider: response.headers['x-provider'] || provider || null,
            language: response.headers['x-language'] || null,
            cueCount: Number(response.headers['x-cue-count']) || 0,
            wordCount: Number(response.headers['x-word-count']) || 0,
            segmentation: response.headers['x-segmentation'] || null,
        };
    } catch (error) {
        // With responseType 'blob' an error body arrives as a Blob too, so the
        // JSON `detail` has to be read back out of it rather than off
        // error.response.data directly.
        const data = error.response?.data;
        if (data instanceof Blob) {
            try {
                const parsed = JSON.parse(await data.text());
                throw new Error(parsed.detail || parsed.error || 'SRT generation failed');
            } catch (parseError) {
                if (parseError instanceof Error && parseError.message !== 'Unexpected end of JSON input') {
                    throw parseError;
                }
            }
        }
        throw new Error(error.message || 'SRT generation failed');
    }
};

// ===== TEMPLATE PREVIEW API FUNCTIONS =====
// Admin-only tool: paste an Edit Plan JSON and preview individual Remotion
// scenes without running the full generation/export pipeline. Proxies to
// the remotion-backend server (port 6100) via the Python backend so the
// browser never needs CORS access or the Remotion API key.
export const validateRemotionPlan = async (plan) => {
    const response = await api.post('/remotion-preview/validate', { plan });
    return response;
};

// Pass `plan` + `sceneIndex` so the render backend derives theme, fps,
// dimensions, captions and watermark from the plan itself — a preview that
// re-sends its own copies of those can silently disagree with the export.
// `scene` (with theme/fps) remains supported for one-off scenes without a plan.
export const previewRemotionScene = async ({ plan, sceneIndex, scene, theme, fps, width, height, format = 'png', frame, voiceover }) => {
    const response = await api.post('/remotion-preview/scene', {
        plan, sceneIndex, scene, theme, fps, width, height, format, frame, voiceover
    });
    return response;
};

export const getRemotionSceneTypes = async () => {
    const response = await api.get('/remotion-preview/scene-types');
    return response;
};

export default api;
// ===== VIDEO WEB CRAWL API FUNCTIONS =====
// Admin-only tool: research licensable clips for a YouTube documentary.
//
// The long timeout is the point. The backend makes ONE request to a model that
// runs a live web search and reads a source page per clip, so a real search is
// minutes rather than seconds — the shared `api` instance's own 50-minute
// ceiling covers it, but this states the intent locally so a later tightening
// of that default cannot silently start cutting searches off.
export const videoWebCrawlSearch = async (query, { clipCount } = {}) => {
    const response = await api.post(
        '/video-web-crawl/search',
        { query, clip_count: clipCount },
        { timeout: 900000 },
    );
    return response;
};

// One serper.dev call, each hit resolved through the HOST'S OWN metadata API to
// a direct file checked to exist, then the top file downloaded and cut into
// 10-second segments on the server. The download and re-encodes make this tens
// of seconds rather than seconds.
export const realFootageSearch = async (query, { count, segments, includeFallback = true } = {}) => {
    const response = await api.post(
        '/video-web-crawl/real-footage',
        { query, count, segments, include_fallback: includeFallback },
    );
    return response;
};

// Segments are served by an admin-gated route, so a plain <a href> cannot
// fetch them — the bearer token has to ride on the request.
export const downloadFootageSegment = async (downloadPath, filename) => {
    const response = await axios.get(`${API_BASE_URL}${downloadPath}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

// B-roll for many scenes in one request — the shape the video pipeline will
// eventually call, exercised here by hand.
//
// The same 900s ceiling as the single search above, and for a stronger reason:
// the server batches scenes at ten per model call and runs those calls
// concurrently, so a fifty-scene request is several web-searching model calls
// deep before it answers.
export const sceneFootageSearch = async (scenes, { clipsPerScene } = {}) => {
    const response = await api.post(
        '/video-web-crawl/scenes',
        { scenes, clips_per_scene: clipsPerScene },
        { timeout: 900000 },
    );
    return response;
};

// AI Avatar (admins and testers): the caller's avatars, each with its variants.
export const listAvatars = async () => api.get('/ai-avatars');

export const createAvatar = async (photo, name) => {
    const formData = new FormData();
    formData.append('photo', photo);
    return api.post(`/ai-avatars?name=${encodeURIComponent(name)}`, formData);
};

export const deleteAvatar = async (avatarId) => api.delete(`/ai-avatars/${avatarId}`);

// A new look of an avatar: its original photo edited by the prompt. Starts 'processing'.
export const createAvatarVariant = async (avatarId, prompt) => api.post(`/ai-avatars/${avatarId}/variants`, { prompt });
